import React, { useEffect, useRef } from "react";

// Canvas-based rain overlay that fades from a full shower to a gentle drizzle
// over `fadeSeconds` seconds, then stays at the softer level. Nothing ever
// stops entirely — a light rhythm remains as a companion.
//
// Props:
//   fadeSeconds  — how long the fade from strong→soft takes (default 90)
//   maxDrops     — initial drop count (default 180)
//   softFactor   — floor intensity 0..1 (default 0.25 = 45 drops out of 180)
//   respectReducedMotion — skips animation if user prefers reduced motion.

const RainOverlay = ({
  fadeSeconds = 90,
  maxDrops = 180,
  softFactor = 0.5,
  respectReducedMotion = true,
}) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const dropsRef = useRef([]);
  const startedAtRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (
      respectReducedMotion &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      // Draw a couple of static faint streaks and stop — no animation.
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w;
      canvas.height = h;
      ctx.strokeStyle = "rgba(200,220,255,0.12)";
      ctx.lineWidth = 1;
      for (let i = 0; i < 20; i++) {
        const x = Math.random() * w;
        const y = Math.random() * h;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 1, y + 8);
        ctx.stroke();
      }
      return () => {};
    }

    let dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const spawn = () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * -window.innerHeight * 0.4,
      len: 10 + Math.random() * 16,
      speed: 5 + Math.random() * 7,
      opacity: 0.28 + Math.random() * 0.34,
    });

    // Pre-populate the drop pool at max size; intensity gates how many render.
    dropsRef.current = Array.from({ length: maxDrops }, spawn);
    startedAtRef.current = performance.now();

    const tick = (now) => {
      const w = window.innerWidth;
      const h = window.innerHeight;

      const elapsed = (now - startedAtRef.current) / 1000;
      const fadeProgress = Math.min(elapsed / fadeSeconds, 1);
      // Linear ramp from 1.0 → softFactor over fadeSeconds.
      const intensity = 1.0 - fadeProgress * (1.0 - softFactor);
      const activeCount = Math.floor(maxDrops * intensity);
      // Opacity also softens slightly so the last drops feel gentler still,
      // but never below a clearly-visible floor.
      const opacityScale = 0.75 + intensity * 0.25;

      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = "rgba(210,225,255,1)";
      ctx.lineWidth = 1.3;

      const drops = dropsRef.current;
      for (let i = 0; i < activeCount; i++) {
        const d = drops[i];
        d.y += d.speed;
        if (d.y - d.len > h) {
          d.x = Math.random() * w;
          d.y = -d.len - Math.random() * 40;
        }
        ctx.globalAlpha = d.opacity * opacityScale;
        ctx.beginPath();
        ctx.moveTo(d.x, d.y);
        // Slight rightward slant like real windblown rain.
        ctx.lineTo(d.x + 1.5, d.y + d.len);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [fadeSeconds, maxDrops, softFactor, respectReducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-[1] pointer-events-none"
      aria-hidden="true"
      data-testid="rain-overlay"
    />
  );
};

export default RainOverlay;
